export const resizeScrollSafeLectureIframeOnLoad = (
  iframe: HTMLIFrameElement,
  setWrapperHeight: (iframe: HTMLIFrameElement, height?: number) => void,
): number | undefined => {
  let height: number | undefined

  try {
    const doc = iframe.contentDocument ?? iframe.contentWindow?.document
    height = doc?.body?.scrollHeight
  } catch {
    height = undefined
  }

  setWrapperHeight(iframe, height)

  if (height) {
    iframe.style.position = 'relative'
    iframe.style.opacity = '1'
  }

  return height
}

// NOTE: MutationObserver-based watcher that keeps links opening in the parent
// - Sets target="_parent" for anchors unless explicitly _blank or excluded
// - Reacts to dynamic DOM changes (added nodes and href/target updates)
// - Returns a cleanup function to disconnect observers and listeners
export const startFixLinksWatcher = (iframe: HTMLIFrameElement | null) => {
  if (!iframe) return () => {}

  const IGNORE_HREF_RE = /^(?:mailto:|tel:|javascript:)/i

  const tryGetDocument = (): Document | null => {
    try {
      return iframe.contentDocument ?? iframe.contentWindow?.document ?? null
    } catch {
      return null
    }
  }

  const getTarget = (a: HTMLAnchorElement): string | undefined =>
    a.getAttribute('target')?.trim().toLowerCase()

  const normalizePathname = (pathname: string): string => pathname.replace(/\/+$/, '') || '/'

  const shouldStayInIframe = (a: HTMLAnchorElement, doc: Document): boolean => {
    const href = a.getAttribute('href')
    if (!href) return false

    const trimmedHref = href.trim()
    if (trimmedHref.startsWith('#')) return false // let anchor-only links target the parent

    let anchorUrl: URL
    try {
      anchorUrl = new URL(href, doc.URL)
    } catch {
      return false
    }

    const docUrl = new URL(doc.URL)
    const sameOrigin = anchorUrl.origin === docUrl.origin
    if (!sameOrigin) return false

    if (anchorUrl.hash) return false // hashed links should bubble to the embedding page

    const samePath = normalizePathname(anchorUrl.pathname) === normalizePathname(docUrl.pathname)
    const sameSearch = anchorUrl.search === docUrl.search
    return samePath && sameSearch
  }

  const initFor = (): (() => void) => {
    const doc = tryGetDocument()
    if (!doc) return () => {}

    const shouldSkip = (a: HTMLAnchorElement): boolean => {
      if (Object.hasOwn(a.dataset, 'siKeepTarget')) return true
      if (getTarget(a) === '_blank') return true
      if (a.hasAttribute('download')) return true
      const href = a.getAttribute('href')
      if (!href) return true
      if (IGNORE_HREF_RE.test(href)) return true
      if (shouldStayInIframe(a, doc)) return true
      return false
    }

    const pending = new Set<HTMLAnchorElement>()
    let scheduled = false

    const processAnchor = (a: HTMLAnchorElement): void => {
      if (shouldSkip(a)) return
      if (getTarget(a) === '_parent') return
      a.setAttribute('target', '_parent')
    }

    const flush = (): void => {
      scheduled = false
      for (const a of pending) processAnchor(a)
      pending.clear()
    }

    const scheduleFlush = (): void => {
      if (scheduled) return
      scheduled = true
      const ric = (
        globalThis as unknown as {
          requestIdleCallback?: (cb: () => void, opts?: { timeout?: number }) => void
        }
      ).requestIdleCallback
      if (typeof ric === 'function') ric(flush, { timeout: 200 })
      else Promise.resolve().then(flush)
    }

    // Initial pass
    doc.querySelectorAll('a[href]').forEach(a => {
      pending.add(a as HTMLAnchorElement)
    })
    scheduleFlush()

    const observer = new MutationObserver(mutations => {
      for (const m of mutations) {
        if (m.type === 'childList') {
          m.addedNodes.forEach(node => {
            if (node.nodeType !== Node.ELEMENT_NODE) return
            const el = node as Element
            if (el.tagName === 'A') {
              const aEl = el as unknown as HTMLAnchorElement
              if (aEl.hasAttribute('href')) pending.add(aEl)
            }
            // Only query when necessary to avoid expensive scans
            el.querySelectorAll?.('a[href]').forEach(a => pending.add(a as HTMLAnchorElement))
          })
        }
        if (m.type === 'attributes' && m.target instanceof HTMLAnchorElement) {
          // Early-bail for href changes on already-fixed links
          if (m.attributeName === 'href' && getTarget(m.target) === '_parent') {
            continue
          }
          pending.add(m.target)
        }
      }
      if (pending.size > 0) scheduleFlush()
    })

    const root: Node =
      (doc.body as Node) || (doc.documentElement as Node) || (doc as unknown as Node)
    observer.observe(root, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['href', 'target', 'download'],
    })

    return () => {
      observer.disconnect()
    }
  }

  let stopObserver: null | (() => void) = null

  const boot = (): void => {
    // tear down previous observer if any
    stopObserver?.()
    stopObserver = initFor()
  }

  iframe.addEventListener('load', boot)
  const doc = tryGetDocument()
  if (doc?.readyState === 'complete') boot()

  return (): void => {
    iframe.removeEventListener('load', boot)
    stopObserver?.()
  }
}
